import { FC, useEffect, useRef, useState } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useFormContext } from "react-hook-form";

import { useRateFieldName } from "../../hooks/useRateFieldName";
import { AmountField } from "../../RateFields/AmountField/AmountField";
import { DescriptionField } from "../../RateFields/DescriptionField/DescriptionField";
import { MaxPerOrderField } from "../../RateFields/MaxPerOrderField/MaxPerOrderField";
import { MinPerOrderField } from "../../RateFields/MinPerOrderField/MinPerOrderField";
import { NameField } from "../../RateFields/NameField/NameField";
import { SeatsField } from "../../RateFields/SeatsField/SeatsField";
import { ExtrasSection } from "../../shared/ExtrasSection/ExtrasSection";
import { ExtrasSummary } from "../../shared/ExtrasSummary/ExtrasSummary";
import { RateCardShell } from "../../shared/RateCardShell/RateCardShell";
import { RateFieldsRow } from "../../shared/RateFieldsRow/RateFieldsRow";
import { CategoriesRow } from "../../shared/RateOptions/OptionRows/CategoriesRow";
import { ScheduleRow } from "../../shared/RateOptions/OptionRows/ScheduleRow/ScheduleRow";
import { GroupTicketRow } from "../../shared/RateOptions/OptionRows/GroupTicketRow/GroupTicketRow";
import { MemberOnlyRow } from "../../shared/RateOptions/OptionRows/MemberOnlyRow";
import { PayWhatYouCanRow } from "../../shared/RateOptions/OptionRows/PayWhatYouCanRow";
import { TaxReceiptRow } from "../../shared/RateOptions/OptionRows/TaxReceiptRow/TaxReceiptRow";
import { useShowTaxReceiptRow } from "../../shared/RateOptions/OptionRows/TaxReceiptRow/useShowTaxReceiptRow";
import { TaxRow } from "../../shared/RateOptions/OptionRows/TaxRow/TaxRow";
import { useShowTaxRow } from "../../shared/RateOptions/OptionRows/TaxRow/useShowTaxRow";
import { RateOptionsGroup } from "../../shared/RateOptions/RateOptionsGroup";
import { RateStatusChip } from "../../shared/RateStatusChip/RateStatusChip";
import { RateSettingSuggestions, RateSuggestionSeed } from "./RateSettingSuggestions/RateSettingSuggestions";

import { useTranslate } from "@/common/hooks/useTranslate";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";

const IDENTITY_FIELDS_DEBOUNCE_MS = 250;

interface EventRateProps {
  totalCardNumber: number;
  shouldDisplayDeleteButton?: boolean;
  shouldConfirmDelete?: boolean;
  onDelete: (index: number) => void;
  onDuplicate: (index: number) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  questionsArchivedOnDelete?: string[];
}

export const EventRate: FC<EventRateProps> = (props) => {
  const { t } = useTranslate();
  const { getValues } = useFormContext<StepTicketsFormData>();
  const showTaxReceiptRow = useShowTaxReceiptRow();
  const showTaxRow = useShowTaxRow();

  const nameFieldName = useRateFieldName("name");
  const descriptionFieldName = useRateFieldName("description");

  const buildSuggestionSeed = (): RateSuggestionSeed => ({
    name: getValues(nameFieldName) ?? "",
    description: getValues(descriptionFieldName) ?? "",
  });

  const [suggestionSeed, setSuggestionSeed] = useState<RateSuggestionSeed>(buildSuggestionSeed);

  const seedRefreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (seedRefreshTimeoutRef.current) {
        clearTimeout(seedRefreshTimeoutRef.current);
      }
    },
    []
  );

  const handleIdentityBlur = () => {
    if (seedRefreshTimeoutRef.current) {
      clearTimeout(seedRefreshTimeoutRef.current);
    }
    seedRefreshTimeoutRef.current = setTimeout(() => {
      setSuggestionSeed((previous) => {
        const next = buildSuggestionSeed();
        if (previous.name === next.name && previous.description === next.description) {
          return previous;
        }
        return next;
      });
    }, IDENTITY_FIELDS_DEBOUNCE_MS + 50);
  };

  return (
    <RateCardShell {...props} statusChip={<RateStatusChip />}>
      <Stack spacing={1.5}>
        <Box onBlur={handleIdentityBlur}>
          <Stack spacing={1.5}>
            <NameField />
            <RateSettingSuggestions seed={suggestionSeed} field="name" />
            <AmountField />
            <DescriptionField />
            <RateSettingSuggestions seed={suggestionSeed} field="description" />
          </Stack>
        </Box>

        <ExtrasSection mode="toggle" variant="options-panel" collapsedSummary={<ExtrasSummary />}>
          <RateOptionsGroup
            title={t("dashboard", "ticketing.rateOptionsGroup.capacity")}
            beforeRows={
              <RateFieldsRow>
                <SeatsField />
                <MinPerOrderField />
                <MaxPerOrderField />
              </RateFieldsRow>
            }
          >
            <ScheduleRow />
            <MemberOnlyRow />
          </RateOptionsGroup>
          <RateOptionsGroup title={t("dashboard", "ticketing.rateOptionsGroup.ticketType")}>
            <PayWhatYouCanRow />
            <GroupTicketRow />
            <CategoriesRow />
          </RateOptionsGroup>
          {(showTaxReceiptRow || showTaxRow) && (
            <RateOptionsGroup title={t("dashboard", "ticketing.rateOptionsGroup.taxesAndReceipts")}>
              {showTaxReceiptRow && <TaxReceiptRow />}
              {showTaxRow && <TaxRow />}
            </RateOptionsGroup>
          )}
        </ExtrasSection>
      </Stack>
    </RateCardShell>
  );
};
